import React from "react";
import { IconType } from "react-icons";
import { LuChevronDown, LuInfo, LuShieldCheck } from "react-icons/lu";
import promiseGraphic from "../../images/futurelab-promise-graphic-v2.webp";

// Shared primitives for the landing page. Tailwind here is desktop-first (see tailwind.config.js):
// unprefixed classes are the desktop case, `sm:` means ≤992px and `xs:` means ≤480px.

export const CONTAINER = "tw-mx-auto tw-max-w-[1180px] tw-px-8 sm:tw-px-5";

// The narrow column for guide-style reading sections.
export const READING = "tw-mx-auto tw-max-w-[980px] tw-px-8 sm:tw-px-5";

// The faint blueprint grid behind the hero sections, fading out towards the bottom.
export const GRID_BACKDROP: React.CSSProperties = {
  backgroundImage:
    "linear-gradient(90deg, #087bd313 1px, transparent 1px), linear-gradient(#087bd313 1px, transparent 1px)",
  backgroundSize: "36px 36px",
  WebkitMaskImage: "linear-gradient(#000, transparent 88%)",
  maskImage: "linear-gradient(#000, transparent 88%)",
};

const BREADCRUMB_TONES = {
  light: { base: "tw-text-[#5b7188]", link: "hover:tw-text-[#087bd3]", current: "tw-text-[#0068b8]" },
  dark: { base: "tw-text-[#b9cddd]", link: "hover:tw-text-white", current: "tw-text-[#70d9ce]" },
} as const;

type Crumb = { label: string; href: string };

export function Breadcrumb({
  current,
  parents = [],
  tone = "light",
}: {
  current: string;
  parents?: readonly Crumb[];
  tone?: keyof typeof BREADCRUMB_TONES;
}) {
  const colours = BREADCRUMB_TONES[tone];
  const trail = [{ label: "Home", href: "/" }, ...parents];

  return (
    <nav aria-label="Breadcrumb" className={`tw-text-sm tw-font-bold ${colours.base}`}>
      <ol className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-items-center tw-gap-2 tw-p-0">
        {trail.map((crumb) => (
          <React.Fragment key={crumb.label}>
            <li>
              <a href={crumb.href} className={`tw-text-inherit tw-no-underline ${colours.link}`}>
                {crumb.label}
              </a>
            </li>
            <li aria-hidden>/</li>
          </React.Fragment>
        ))}
        <li aria-current="page" className={colours.current}>
          {current}
        </li>
      </ol>
    </nav>
  );
}

// Form-field styling for the business case wizard, kept in one place so the text inputs, number
// inputs and the objective select stay identical.
export const FIELD_LABEL = "tw-mb-2 tw-block tw-text-sm tw-font-bold tw-leading-5 tw-text-[#304d68]";
export const FIELD_HINT = "tw-mt-2 tw-mb-0 tw-text-xs tw-leading-5 tw-text-[#5b7188]";
export const FIELD_CONTROL =
  "tw-h-12 tw-w-full tw-min-w-0 tw-rounded-xl tw-border tw-border-[#c9deed] tw-bg-white tw-px-3 tw-text-base tw-text-[#10233f] tw-shadow-sm tw-transition-colors placeholder:tw-text-[#93a9bd] focus:tw-border-[#087bd3] focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#087bd3]/30";

// A number input keeps a typed leading zero on screen ("030") because React compares its value
// loosely, so the text is normalised before the number is read.
const LEADING_ZEROS = /^(-?)0+(?=\d)/;

export function readNumberField(event: React.ChangeEvent<HTMLInputElement>): number {
  const input = event.target;
  const normalized = input.value.replace(LEADING_ZEROS, "$1");

  if (normalized !== input.value) input.value = normalized;

  return input.valueAsNumber;
}

// Colour lives in the variant map, not a caller className: `important: true` makes a passed-in
// background win or lose on stylesheet order.
const TILE_VARIANTS = {
  soft: "tw-bg-[#eaf6fd] tw-text-[#087bd3]",
  solid: "tw-bg-[#087bd3] tw-text-white",
  dark: "tw-bg-[#10233f] tw-text-white",
  mint: "tw-bg-[#55cfc1] tw-text-[#10233f]",
} as const;

export type TileVariant = keyof typeof TILE_VARIANTS;

export function IconTile({
  icon: Icon,
  variant = "soft",
  className = "",
}: {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  variant?: TileVariant;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={`tw-inline-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl ${TILE_VARIANTS[variant]} ${className}`}
    >
      <Icon className="tw-h-5 tw-w-5" />
    </span>
  );
}

// Tone carries the surface the panel sits on, not its severity: `info` for the white form card,
// `inverse` for the dark estimate panel.
const NOTICE_TONES = {
  info: "tw-border-[#a9d3f0] tw-bg-[#e9f4fd] tw-text-[#0b5c96]",
  inverse: "tw-border-white/25 tw-bg-white/10 tw-text-[#d7e6f3]",
} as const;

export type NoticeTone = keyof typeof NOTICE_TONES;

export function Notice({
  id,
  tone = "info",
  icon: Icon = LuInfo,
  className = "",
  children,
}: {
  id?: string;
  tone?: NoticeTone;
  icon?: IconType;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      id={id}
      role="status"
      className={`tw-flex tw-items-start tw-gap-3 tw-rounded-2xl tw-border tw-p-4 ${NOTICE_TONES[tone]} ${className}`}
    >
      <Icon aria-hidden className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
      <p className="tw-m-0 tw-text-sm tw-font-bold tw-leading-6">{children}</p>
    </div>
  );
}

// Every select on the landing site goes through here. A native select keeps the browser's own
// arrow, which sits hard against the border in Chrome and belongs to no design system.
export function SelectField({
  id,
  name,
  label,
  value,
  placeholder,
  className = "",
  options,
  invalid = false,
  describedBy,
  onChange,
}: {
  id: string;
  name?: string;
  label: string;
  value: string;
  placeholder?: string;
  className?: string;
  options: readonly { value: string; label: string }[];
  invalid?: boolean;
  describedBy?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className={`tw-min-w-0 ${className}`}>
      <label htmlFor={id} className={FIELD_LABEL}>
        {label}
      </label>
      <div className="tw-relative">
        <select
          id={id}
          name={name}
          required
          value={value}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
          className={`${FIELD_CONTROL} tw-cursor-pointer tw-appearance-none tw-pr-11 ${
            value ? "" : "tw-text-[#93a9bd]"
          }`}
        >
          {placeholder ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {options.map((option) => (
            <option key={option.value} value={option.value} className="tw-text-[#10233f]">
              {option.label}
            </option>
          ))}
        </select>
        <LuChevronDown
          aria-hidden
          className="tw-pointer-events-none tw-absolute tw-right-4 tw-top-1/2 tw--translate-y-1/2 tw-h-4 tw-w-4 tw-text-[#5b7188]"
        />
      </div>
    </div>
  );
}

// A bare #anchor jumps the length of a marketing page in one frame. Motion is opt-out, and focus
// follows the scroll so keyboard users carry on from the section they asked for.
export function jumpTo(id: string) {
  return (event: React.MouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById(id);

    if (!target || event.defaultPrevented) return;

    event.preventDefault();

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    target.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
    target.setAttribute("tabindex", "-1");
    target.classList.add("tw-outline-none");
    target.focus({ preventScroll: true });
    window.history.replaceState(null, "", `#${id}`);
  };
}

type SectionProps = {
  id?: string;
  className?: string;
  children: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLElement>, "className" | "id" | "children">;

export function Section({ id, className = "", children, ...rest }: SectionProps) {
  return (
    <section id={id} className={`tw-py-28 sm:tw-py-12 ${className}`} {...rest}>
      <div className={CONTAINER}>{children}</div>
    </section>
  );
}

// Same reason as the tile and button maps: `important: true` makes a colour passed in by a caller
// win or lose on stylesheet order, so the dark-section variant lives here.
const LABEL_TONES = {
  brand: "tw-text-[#0068b8]",
  mint: "tw-text-[#70d9ce]",
} as const;

export type LabelTone = keyof typeof LABEL_TONES;

export function SectionLabel({
  tone = "brand",
  children,
}: {
  tone?: LabelTone;
  children: React.ReactNode;
}) {
  return (
    <p className={`tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.15em] ${LABEL_TONES[tone]}`}>
      {children}
    </p>
  );
}

// `compact` is the desktop size for a heading that sits in a narrow side-by-side column, where the
// display size breaks into five ragged lines rather than three.
const HEADING_SIZES = {
  display: "tw-text-5xl tw-leading-[1.04]",
  compact: "tw-text-[2.25rem] tw-leading-[1.1]",
} as const;

export type HeadingSize = keyof typeof HEADING_SIZES;

export function SectionHeading({
  id,
  size = "display",
  children,
}: {
  id?: string;
  size?: HeadingSize;
  children: React.ReactNode;
}) {
  return (
    <h2
      id={id}
      className={`tw-mt-4 tw-mb-0 tw-font-black tw-tracking-[-.045em] ${HEADING_SIZES[size]} sm:tw-text-[2.25rem] sm:tw-leading-[1.1]`}
    >
      {children}
    </h2>
  );
}

const PRIMARY_SHAPE =
  "tw-inline-flex tw-shrink-0 tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-font-bold tw-no-underline tw-transition-colors focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2";

// Colour lives here rather than in a caller's className: the config sets `important: true`, so an
// override passed in wins or loses on stylesheet order and can end up white on white.
const PRIMARY_VARIANTS = {
  solid:
    "tw-bg-[#0068b8] tw-text-white hover:tw-bg-[#005a9f] hover:tw-text-white focus-visible:tw-outline-[#10233f]",
  inverse:
    "tw-bg-white tw-text-[#0068b8] hover:tw-bg-[#edf8ff] hover:tw-text-[#0068b8] focus-visible:tw-outline-white",
} as const;

export type PrimaryVariant = keyof typeof PRIMARY_VARIANTS;

const primaryClasses = (variant: PrimaryVariant) => `${PRIMARY_SHAPE} ${PRIMARY_VARIANTS[variant]}`;

export function PrimaryLink({
  href,
  variant = "solid",
  className = "",
  children,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string; variant?: PrimaryVariant }) {
  return (
    <a
      href={href}
      className={`${primaryClasses(variant)} tw-h-14 tw-px-7 tw-text-base ${
        variant === "solid" ? "tw-shadow-[0_12px_30px_rgba(8,123,211,.28)]" : ""
      } ${className}`}
      {...rest}
    >
      {children}
    </a>
  );
}

// Same reason as PRIMARY_VARIANTS: colour and shape live here so a caller's className cannot win
// or lose on stylesheet order. Shared by the link and the button below.
const SECONDARY_SHAPE =
  "tw-inline-flex tw-shrink-0 tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-border-[#b8cee2] tw-bg-white tw-px-7 tw-text-base tw-font-bold tw-text-[#10233f] tw-transition-colors hover:tw-bg-[#eaf4fc] focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-[#10233f]";

export function SecondaryLink({
  href,
  className = "",
  children,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  return (
    <a
      href={href}
      className={`${SECONDARY_SHAPE} tw-h-14 tw-no-underline hover:tw-text-[#10233f] ${className}`}
      {...rest}
    >
      {children}
    </a>
  );
}

// `lg` matches PrimaryLink, for the buttons that replaced a link-shaped CTA in the same slot.
const BUTTON_SIZES = {
  md: "tw-min-h-12 tw-px-7 tw-py-3 tw-text-sm",
  lg: "tw-min-h-14 tw-px-7 tw-py-4 tw-text-base",
} as const;

export type ButtonSize = keyof typeof BUTTON_SIZES;

export function PrimaryButton({
  variant = "solid",
  size = "md",
  className = "",
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: PrimaryVariant;
  size?: ButtonSize;
}) {
  return (
    <button
      className={`${primaryClasses(variant)} tw-h-auto tw-whitespace-normal ${BUTTON_SIZES[size]} ${
        variant === "solid" ? "tw-border-0" : "tw-border tw-border-[#b8cee2]"
      } disabled:tw-pointer-events-none disabled:tw-opacity-50 ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

// The button twin of SecondaryLink, for CTAs that now open the enquiry dialog instead of navigating.
export function SecondaryButton({
  className = "",
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={`${SECONDARY_SHAPE} tw-min-h-14 tw-py-4 tw-whitespace-normal ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export function GhostButton({
  className = "",
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={`tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-border-[#b8cee2] tw-bg-white tw-min-h-12 tw-px-6 tw-py-3 tw-text-sm tw-font-bold tw-text-[#10233f] tw-transition-colors hover:tw-bg-[#eaf4fc] disabled:tw-pointer-events-none disabled:tw-opacity-50 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-[#10233f] ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

// The promise card's blue side, shared by the landing banner and the pricing page. The artwork's
// own background is this exact blue, so it bleeds to the panel edges with no visible frame; the
// scrim covers the crop at the bottom and deepens enough for the wording to clear AA over it.
export function PromisePanel({ children }: { children: React.ReactNode }) {
  return (
    <div className="tw-relative tw-flex tw-min-h-[28rem] tw-flex-col tw-justify-end tw-overflow-hidden tw-bg-[#087bd3] tw-p-10 tw-text-white sm:tw-min-h-[24rem] sm:tw-p-8 xs:tw-min-h-[20rem]">
      <img
        src={promiseGraphic}
        alt=""
        loading="lazy"
        decoding="async"
        className="tw-pointer-events-none tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover tw-object-[50%_35%]"
      />
      <div
        aria-hidden
        className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-[46%] tw-bg-[linear-gradient(to_top,#0a5fa8_0%,#0b64ad_34%,rgba(8,123,211,.94)_60%,rgba(8,123,211,.5)_80%,rgba(8,123,211,0)_100%)]"
      />
      <div className="tw-relative">
        <p className="tw-m-0 tw-flex tw-items-center tw-gap-2.5 tw-text-sm tw-font-black tw-uppercase tw-tracking-[.15em] tw-text-[#d5eeff]">
          <LuShieldCheck aria-hidden className="tw-h-5 tw-w-5 tw-shrink-0" />
          The FutureLab promise
        </p>
        <p className="tw-mt-3 tw-mb-0 tw-text-3xl tw-font-black tw-leading-tight tw-tracking-[-.04em]">
          {children}
        </p>
      </div>
    </div>
  );
}
